import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';

import {
  fetchSpotRecommendations,
  getSpotRecommendationsUnitsKey,
  type SpotRecommendation,
  type SpotRecommendationsResponse,
  type SpotRecommendationsUnits,
} from 'common/api/spotRecommendations';

export const SPOT_RECOMMENDATIONS_PAGE_SIZE = 5;

export const getSpotRecommendationsKey = (
  lat: number,
  lon: number,
  subregionId: string | null,
  pageIndex: number,
  units?: SpotRecommendationsUnits,
): [
  string,
  number,
  number,
  string | null,
  number,
  ReturnType<typeof getSpotRecommendationsUnitsKey>,
] => [
  'spot-recommendations',
  lat,
  lon,
  subregionId,
  pageIndex,
  getSpotRecommendationsUnitsKey(units),
];

interface UseSpotRecommendationsParams {
  lat: number;
  lon: number;
  subregionId?: string;
  enabled: boolean;
  units?: SpotRecommendationsUnits;
}

interface UseSpotRecommendationsResult {
  recommendations: Array<SpotRecommendation>;
  fetchNextPage: () => Promise<void>;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  hasReachedEnd: boolean;
}

const useSpotRecommendations = ({
  lat,
  lon,
  subregionId,
  enabled,
  units,
}: UseSpotRecommendationsParams): UseSpotRecommendationsResult => {
  const getKey = useCallback(
    (pageIndex: number, previousPage: SpotRecommendationsResponse | null) => {
      if (!enabled) return null;
      if (previousPage && previousPage.recommendations.length < SPOT_RECOMMENDATIONS_PAGE_SIZE) {
        return null;
      }
      return getSpotRecommendationsKey(lat, lon, subregionId ?? null, pageIndex, units);
    },
    [enabled, lat, lon, subregionId, units],
  );

  const { data, error, size, setSize, isLoading, isValidating } =
    useSWRInfinite<SpotRecommendationsResponse>(
      getKey,
      ([, latVal, lonVal, subregion, pageIndex]) =>
        fetchSpotRecommendations({
          lat: latVal as number,
          lon: lonVal as number,
          subregionId: (subregion as string | null) ?? undefined,
          offset: (pageIndex as number) * SPOT_RECOMMENDATIONS_PAGE_SIZE,
          limit: SPOT_RECOMMENDATIONS_PAGE_SIZE,
          units,
        }),
      { revalidateOnFocus: false, revalidateFirstPage: false, keepPreviousData: true },
    );

  // Deduplicates across pages since the API may return overlapping results
  const recommendations = useMemo<Array<SpotRecommendation>>(() => {
    if (!data) return [];
    const seen = new Set<string>();
    const result: Array<SpotRecommendation> = [];
    for (const page of data) {
      for (const rec of page.recommendations) {
        if (!seen.has(rec._id)) {
          seen.add(rec._id);
          result.push(rec);
        }
      }
    }
    return result;
  }, [data]);

  // No more pages when the first page is empty or the last page returned fewer than PAGE_SIZE items
  const lastPage = data?.[data.length - 1];
  const hasReachedEnd =
    !!data &&
    (data[0]?.recommendations.length === 0 ||
      (!!lastPage && lastPage.recommendations.length < SPOT_RECOMMENDATIONS_PAGE_SIZE));
  const isLoadingMore =
    isValidating || (size > 0 && !!data && typeof data[size - 1] === 'undefined');

  const fetchNextPage = useCallback(async () => {
    if (hasReachedEnd || isLoadingMore) return;
    await setSize((s) => s + 1);
  }, [hasReachedEnd, isLoadingMore, setSize]);

  return {
    recommendations,
    fetchNextPage,
    isLoading,
    isLoadingMore,
    isError: !!error,
    hasReachedEnd,
  };
};

export default useSpotRecommendations;
